import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

export interface SmoothScrollOptions {
  /** The CSS selector or element to scroll to */
  scrollTarget: string | Element;
  /** Duration of the scroll animation in milliseconds (default: 500) */
  duration?: number;
  /** Offset from the target element in pixels (default: 0) */
  scrollOffset?: number;
  /** Document to scroll in (default: window.document) */
  document?: Document;
  /** Container element to scroll within (default: window) */
  scrollContainer?: Element | Window;
}

@Injectable({
  providedIn: 'root'
})
export class SmoothScrollService {

  constructor(@Inject(DOCUMENT) private document: Document) {}

  /**
   * Smoothly scrolls to a target element
   * @param options Configuration options for the scroll behavior
   * @returns Promise that resolves when scrolling is complete
   */
  public scroll(options: SmoothScrollOptions): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const {
          scrollTarget,
          duration = 500,
          scrollOffset = 0,
          document: targetDocument = this.document,
          scrollContainer = window
        } = options;

        // Get the target element
        let targetElement: Element | null = null;
        
        if (typeof scrollTarget === 'string') {
          targetElement = targetDocument.querySelector(scrollTarget);
        } else {
          targetElement = scrollTarget;
        }

        if (!targetElement) {
          reject(new Error(`Target element not found: ${scrollTarget}`));
          return;
        }

        // Wait for next frame to ensure layout is stable
        requestAnimationFrame(() => {
          try {
            // Get the initial scroll position and target position
            const startPosition = this.getScrollPosition(scrollContainer);
            const initialTargetPosition = this.getTargetPosition(targetElement!, scrollContainer, scrollOffset);
            const initialDistance = initialTargetPosition - startPosition;
            
            // If no scrolling is needed, resolve immediately
            if (Math.abs(initialDistance) < 1) {
              resolve();
              return;
            }

            // Easing function for smooth animation (easeInOutCubic)
            const easeInOutCubic = (t: number): number => {
              return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
            };

            let startTime: number | null = null;

            const animateScroll = (currentTime: number) => {
              if (startTime === null) {
                startTime = currentTime;
              }

              const timeElapsed = currentTime - startTime;
              const progress = Math.min(timeElapsed / duration, 1);
              const easeProgress = easeInOutCubic(progress);
              
              // Use original animation path
              const targetPosition = startPosition + (initialDistance * easeProgress);
              this.setScrollPosition(scrollContainer, targetPosition);

              if (progress < 1) {
                requestAnimationFrame(animateScroll);
              } else {
                // Final adjustment to ensure we're exactly at the current target position
                setTimeout(() => {
                  const finalTargetPosition = this.getTargetPosition(targetElement!, scrollContainer, scrollOffset);
                  const currentPosition = this.getScrollPosition(scrollContainer);
                  
                  // Only adjust if we're significantly off (more than 5px)
                  if (Math.abs(finalTargetPosition - currentPosition) > 5) {
                    this.setScrollPosition(scrollContainer, finalTargetPosition);
                  }
                  resolve();
                }, 16); // Wait one frame before final adjustment
              }
            };

            requestAnimationFrame(animateScroll);
          } catch (error) {
            reject(error);
          }
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Scrolls to a target element with default options
   * @param target CSS selector or element
   * @param offset Scroll offset in pixels (default: 0)
   * @param duration Animation duration in milliseconds (default: 500)
   */
  public scrollTo(target: string | Element, offset: number = 0, duration: number = 500): Promise<void> {
    return this.scroll({
      scrollTarget: target,
      scrollOffset: offset,
      duration
    });
  }

  /**
   * Gets the current scroll position of the container
   */
  private getScrollPosition(container: Element | Window): number {
    if (container === window) {
      return window.pageYOffset || this.document.documentElement.scrollTop || this.document.body.scrollTop || 0;
    } else {
      return (container as Element).scrollTop;
    }
  }

  /**
   * Sets the scroll position of the container
   */
  private setScrollPosition(container: Element | Window, position: number): void {
    if (container === window) {
      window.scrollTo(0, position);
    } else {
      (container as Element).scrollTop = position;
    }
  }

  /**
   * Calculates the target scroll position for the element
   */
  private getTargetPosition(element: Element, container: Element | Window, offset: number): number {
    if (container === window) {
      // For window scrolling, use a hybrid approach for better accuracy
      const elementRect = element.getBoundingClientRect();
      const currentScroll = this.getScrollPosition(container);
      
      // Use getBoundingClientRect for more accurate current position
      return currentScroll + elementRect.top - offset;
    } else {
      // For container scrolling, use offsetTop method
      const containerElement = container as Element;
      const elementOffsetTop = this.getElementOffsetTop(element, containerElement);
      return elementOffsetTop - offset;
    }
  }

  /**
   * Gets the absolute offset top of an element relative to a container
   */
  private getElementOffsetTop(element: Element, container: Element): number {
    // Use getBoundingClientRect for more reliable positioning
    const elementRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    
    // Calculate relative position within the container
    const relativeTop = elementRect.top - containerRect.top;
    
    // Add the container's current scroll position
    const containerScrollTop = container.scrollTop;
    
    return containerScrollTop + relativeTop;
  }
}